import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Badge,
  Button,
  Loader,
  Popover,
  Stack,
  Tooltip,
  Tree,
  VStack,
  Whisper,
  WhisperInstance
} from 'rsuite';
import { SearchInput } from './SearchInput';
import { useAppSelector, useAppDispatch } from '@monorepo/shared/store/hook';
import {
  getPreSelectedNode,
  selectOU,
  selectOUStatus,
  selectLineNodes,
  selectAccountOu,
  getAllOuNodes,
  updateSelected,
  getSelectedScopeTrees
} from '@monorepo/shared/store/fcOuSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { copyObj, useQuery } from '@monorepo/shared/utils/helper';
import { BsCloudCheck } from 'react-icons/bs';
import { HiOutlineUserGroup } from 'react-icons/hi';
import ArrowDownIcon from '@rsuite/icons/ArrowDown';

import '../styles/OUTree.scss';
import { t } from 'i18next';
import {
  selectAccountWithPermission,
  updateCurrentAccount,
  updateIsRedirectChecked
} from '../store/accountSlice';
import { selectHasGroup } from '../store/groupSlice';
import { getLocalSelectedOU } from '../services';
import { selectRegions } from '../store/authSlice';
export const getNodeIcon = (node: Record<string, any>) => {
  return node?.rootNode ? (
    <i
      className="icon-fontello-rootfolder"
      style={{ fontSize: '22px', color: '#848baa' }}
    />
  ) : node?.data?.node_type === 'org' ? (
    <i
      className="icon-fontello-folder"
      style={{ fontSize: '20px', color: '#848baa' }}
    />
  ) : (
    <i
      className="icon-fontello-doc"
      style={{ fontSize: '20px', color: '#848baa' }}
    />
  );
};

const LARGE_TREE_THRESHOLD = 500;

const OUTREE_ROW_HEIGHT = 56;

const toExpandValues = (idPath?: string[]) => {
  const path = idPath || [];
  return path.map((_, index) => path.slice(0, index + 1).join('//'));
};

const cloneTreeData = (nodes: any[]): any[] =>
  nodes.map((node) => ({
    ...node,
    children: node?.children?.length
      ? cloneTreeData(node.children)
      : node?.children
  }));

// Matches a node against the search text using its own label / description.
const isNodeMatch = (
  searchText: string,
  node: Record<string, any>
): boolean => {
  const q = searchText.toLowerCase();
  return [node.label, node.desc].some(
    (s) => s && typeof s === 'string' && s.toLowerCase().includes(q)
  );
};

const filterTreeBySearch = (
  nodes: any[],
  searchText: string
): { list: any[]; matched: boolean } => {
  const list: any[] = [];
  let subtreeMatched = false;
  for (const node of nodes) {
    const selfMatch = isNodeMatch(searchText, node);
    const childRes = node?.children?.length
      ? filterTreeBySearch(node.children, searchText)
      : { list: [], matched: false };
    if (selfMatch || childRes.matched) {
      list.push({
        ...node,
        children: selfMatch ? node.children : childRes.list
      });
      subtreeMatched = true;
    }
  }
  return { list, matched: subtreeMatched };
};

interface IProps {
  setOpen?: any;
  selectedNode?: Record<string, any>;
  ignoreGroup?: boolean;
  isNewPortal?: boolean;
  hasPermissionScope?: boolean;
}

type TreeNodeData = Record<string, any>;

export const OUTreeContent = ({
  setOpen,
  selectedNode,
  ignoreGroup,
  isNewPortal,
  hasPermissionScope
}: IProps) => {
  let query = useQuery();
  const { pathname, state } = useLocation();
  const debug_key = query.get('debug_key') || state?.debug_key;
  const localSelectedOU = getLocalSelectedOU();
  const selectedIdPath = selectedNode?.idPath?.length
    ? selectedNode.idPath
    : localSelectedOU?.idPath;
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccountWithPermission);
  const ouData = useAppSelector(selectOU);
  const accountOu = useAppSelector(selectAccountOu);
  const ouDataStatus = useAppSelector(selectOUStatus);
  const lineNodes = useAppSelector(selectLineNodes);
  const hasGroup = useAppSelector(selectHasGroup);
  const regions = useAppSelector(selectRegions);

  const allOuNodes = useMemo(() => {
    const srcData = ouData?.length ? ouData : accountOu;
    return srcData?.length ? getAllOuNodes(srcData) : [];
  }, [ouData, accountOu]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [disableTreeNode, setDisableTreeNode] = useState<string[]>([]);
  const [defaultExpandItemValues, setDefaultExpandItemValues] = useState<
    string[]
  >([]);
  const [defaultExpandAll, setDefaultExpandAll] = useState<boolean>(true);
  const isVirtualized = useMemo(() => {
    let count = 0;
    const stack: any[] = [...treeData];
    while (stack.length) {
      const node = stack.pop();
      count += 1;
      if (count > LARGE_TREE_THRESHOLD) {
        return true;
      }
      if (node?.children?.length) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push(node.children[i]);
        }
      }
    }
    return false;
  }, [treeData]);
  const filteredTree = useMemo(() => {
    if (!searchText.trim()) {
      return treeData;
    }
    return filterTreeBySearch(treeData, searchText).list;
  }, [searchText, treeData]);

  const searchExpandItemValues = useMemo(() => {
    if (!searchText.trim() || !filteredTree.length) {
      return undefined;
    }
    const values: any[] = [];
    const stack: any[] = [...filteredTree];
    while (stack.length) {
      const node = stack.pop();
      if (node?.children?.length) {
        values.push(node.value);
        stack.push(...node.children);
      }
    }
    return values;
  }, [searchText, filteredTree]);
  const navigate = useNavigate();
  const onSelect = (activeNode: TreeNodeData) => {
    const account_id = activeNode?.data?.account_id
      ? `${activeNode.data?.account_id}`
      : '';
    if (account_id) {
      // need keep these two update for select the correct account
      dispatch(updateSelected(copyObj(activeNode)));
      dispatch(updateCurrentAccount(activeNode?.data));
      const data = activeNode?.data;
      let isRedirect: boolean = !(hasGroup && !ignoreGroup && !isNewPortal);

      isRedirect && dispatch(updateIsRedirectChecked(false));
      const accessFrom =
        data?.access_from ||
        (data?.access_from_user_id
          ? {
              account_id: data?.access_from_account_id,
              user_id: data?.access_from_user_id
            }
          : undefined);
      navigate(`/organization/account`, {
        state: {
          debug_key,
          account_id,
          redirect_obj: isRedirect
            ? {
                account_id: data?.account_id,
                user_id: data?.user_id,
                org_id: data?.org_id,
                node_id: data?.account_node_id,
                access_from: accessFrom
              }
            : {}
        }
      });
    }
    if (setOpen) {
      setOpen(false);
    }
  };
  useEffect(() => {
    if (ouData.length || accountOu.length) {
      const sourceData = ouData?.length ? ouData : accountOu;
      setTreeData(cloneTreeData(sourceData));
      const disabledNodeItemValues = allOuNodes
        .filter((obj: Record<any, any>) => !obj?.data?.account_id)
        .map((obj: Record<any, any>) => obj?.value);
      setDisableTreeNode(disabledNodeItemValues);

      setDefaultExpandAll(
        ouData?.length <= 1 && allOuNodes.length <= LARGE_TREE_THRESHOLD
      );
      if (ouData.length > 1 || allOuNodes.length > LARGE_TREE_THRESHOLD) {
        setDefaultExpandItemValues(toExpandValues(selectedIdPath));
      }
    }
  }, [ouData, accountOu, allOuNodes]);

  useEffect(() => {
    if (ouData?.length && hasPermissionScope) {
      const scopedTree = getSelectedScopeTrees(
        account?.permission_scope,
        ouData
      );
      if (scopedTree) {
        const sourceData = [scopedTree];
        setTreeData(cloneTreeData(sourceData));
        setDefaultExpandItemValues(
          Array.from(
            new Set([
              ...toExpandValues(scopedTree.idPath),
              ...toExpandValues(selectedIdPath)
            ])
          )
        );
      }
    }
  }, [hasPermissionScope, account?.permission_scope]);

  const styleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const treeRootRef = useRef<HTMLDivElement | null>(null);
  const updateOUTreeStyle = useCallback(
    (rootNode: any) => {
      if (rootNode) {
        const lines = rootNode.querySelectorAll('.rs-tree-indent-line');
        const items = rootNode.querySelectorAll('.rs-tree-node');
        const children = Object.values(lineNodes)
          .map((obj: any) => obj?.children)
          .flat(1)
          .map((obj: any) => obj.label + obj.level);

        items?.forEach((node: any) => {
          if (node) {
            const level = node?.getAttributeNode('aria-level')?.value;
            const label = node?.getAttributeNode('aria-label')?.value;
            if (level > 1 && !isVirtualized) {
              node.style.paddingLeft = 12 + 28 * (level - 1) + 'px';
            }
            if (children?.includes(label + level)) {
              node.classList.add('node-has-line');
            }
          }
        });
        lines?.forEach((line: any) => {
          if (line) {
            const parent = line?.parentNode?.parentNode;
            const node = parent?.childNodes[0];
            const level = node?.getAttributeNode('aria-level')?.value;
            const label = node?.getAttributeNode('aria-label')?.value;
            if (level > 1) {
              line.style.left = 22 + 28 * level + 'px';
            }
            if (!Object.keys(lineNodes).includes(label + level)) {
              line.style.display = 'none';
            }
          }
        });
      }
    },
    [lineNodes, isVirtualized]
  );

  const scheduleOUTreeStyle = useCallback(() => {
    if (styleTimerRef.current) {
      clearTimeout(styleTimerRef.current);
    }
    styleTimerRef.current = setTimeout(() => {
      styleTimerRef.current = null;
      if (treeRootRef.current) {
        updateOUTreeStyle(treeRootRef.current);
      }
    }, 120);
  }, [updateOUTreeStyle]);
  // In virtualized mode rows mount/unmount on scroll, so re-apply the connector
  // styling (debounced) as the user scrolls.
  const handleTreeScroll = useCallback(() => {
    scheduleOUTreeStyle();
  }, [scheduleOUTreeStyle]);
  const rootRef = useCallback(
    (node: any) => {
      treeRootRef.current = node;
      if (node !== null) {
        scheduleOUTreeStyle();
      }
    },
    [searchText, scheduleOUTreeStyle]
  );

  useEffect(() => {
    scheduleOUTreeStyle();
  }, [treeData, scheduleOUTreeStyle]);

  return (
    <div className="ou-tree-wrapper">
      {ouDataStatus === 'succeeded' ? (
        treeData.length ? (
          <div
            className={`ou-tree-content${
              isVirtualized ? ' ou-tree-virtualized' : ''
            }${searchText ? ' ou-tree-searching' : ''}`}
            ref={rootRef}
          >
            <SearchInput
              placeholder={`${t('common.ouTree.searchInput')}`}
              setValue={setSearchText}
            />
            {searchText.trim() && !filteredTree.length ? (
              <div className="ou-tree-no-results">
                {t('common.ouTree.noData')}
              </div>
            ) : (
              <Tree
                className="ou-tree"
                onSelect={onSelect}
                data={filteredTree}
                disabledItemValues={disableTreeNode}
                showIndentLine
                virtualized={isVirtualized}
                listProps={{ itemSize: OUTREE_ROW_HEIGHT }}
                onScroll={handleTreeScroll}
                expandItemValues={searchExpandItemValues}
                defaultExpandAll={defaultExpandAll}
                defaultExpandItemValues={defaultExpandItemValues}
                renderTreeNode={(node: Record<string, any>) => {
                  const nodeStyle: CSSProperties = {
                    '--gap': `${node?.level * 10}px`
                  } as CSSProperties;
                  const access_from = node?.data.access_from;

                  return (
                    <Stack
                      className={`ou-node ${
                        selectedNode?.idPath?.join('//') ===
                        node?.idPath?.join('//')
                          ? 'active'
                          : ''
                      } ${node?.data?.account_id ? 'account_node' : ''}`}
                      spacing={8}
                      alignItems="stretch"
                      style={nodeStyle}
                    >
                      <Stack.Item className="ou-node-icon">
                        {getNodeIcon(node)}
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Stack
                          justifyContent="space-between"
                          style={{ paddingRight: '20px' }}
                          className="ou-node-item"
                        >
                          <VStack
                            alignItems="flex-start"
                            className="ou-node-content"
                          >
                            <span className="ou-title">
                              {node.rootNode && access_from
                                ? [
                                    access_from.account_id,
                                    access_from.idp_role_name,
                                    node?.label
                                  ]
                                    .filter((a) => a)
                                    .join(' | ')
                                : node?.label}
                              {node.rootNode && hasGroup && (
                                <Whisper
                                  placement="top"
                                  controlId="control-id-hover"
                                  trigger="hover"
                                  speaker={
                                    <Tooltip>{'Group View Enabled'}</Tooltip>
                                  }
                                >
                                  <span>
                                    <HiOutlineUserGroup
                                      style={{
                                        color: 'green',
                                        fontSize: '20px',
                                        marginLeft: '10px',
                                        verticalAlign: 'text-bottom'
                                      }}
                                    ></HiOutlineUserGroup>
                                  </span>
                                </Whisper>
                              )}
                              {node?.data?.user_type === 'Master' && (
                                <Badge
                                  style={{
                                    marginLeft: '10px',
                                    fontSize: '0.8em',
                                    fontWeight: 'bold',
                                    padding: '1px 10px',
                                    borderRadius: '10px'
                                  }}
                                  content={'Master User'}
                                />
                              )}
                              {node?.data?.account_id &&
                                node?.data?.node_id ===
                                  localSelectedOU?.data?.node_id &&
                                node?.data?.root_account ===
                                  localSelectedOU?.data?.root_account && (
                                  <Badge
                                    style={{
                                      marginLeft: '10px',
                                      fontSize: '0.8em',
                                      fontWeight: 'bold',
                                      padding: '1px 10px',
                                      borderRadius: '10px',
                                      backgroundColor: 'transparent',
                                      border: '1px solid #307fe2',
                                      color: '#307fe2'
                                    }}
                                    content={'current Account'}
                                  />
                                )}
                            </span>
                            <Stack spacing={20} alignItems="center">
                              <span className="ou-desc">{node?.desc}</span>
                              <Stack spacing={8}>
                                {node?.data?.account_id &&
                                  (node?.data?.has_instance ||
                                    node?.data?.region?.url) && (
                                    <Whisper
                                      placement="top"
                                      controlId="control-id-hover"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip>
                                          {'Instance Provisioned'}
                                        </Tooltip>
                                      }
                                    >
                                      <span>
                                        <BsCloudCheck
                                          style={{
                                            color: 'green',
                                            fontSize: '20px',
                                            verticalAlign: 'bottom'
                                          }}
                                        ></BsCloudCheck>
                                      </span>
                                    </Whisper>
                                  )}
                                {node?.data?.account_id &&
                                  node?.data?.need_force_upgrade && (
                                    <Whisper
                                      placement="top"
                                      controlId="control-id-hover"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip>
                                          {'Instance Need Force Upgrade'}
                                        </Tooltip>
                                      }
                                    >
                                      <span>
                                        <i
                                          className="icon-fortinetone-upgradeIcon"
                                          style={{
                                            color: '#f44336',
                                            fontSize: '18px',
                                            verticalAlign: 'text-top'
                                          }}
                                        ></i>
                                      </span>
                                    </Whisper>
                                  )}
                              </Stack>
                            </Stack>
                          </VStack>
                          {node?.data?.account_id && (
                            <Button
                              appearance="primary"
                              size="xs"
                              className="select-btn"
                            >
                              select
                            </Button>
                          )}
                        </Stack>
                      </Stack.Item>
                    </Stack>
                  );
                }}
              />
            )}
          </div>
        ) : (
          <div className="no-data">{t('common.ouTree.noData')}</div>
        )
      ) : (
        <Loader center content={`${t('common.ouTree.loading')}`} />
      )}
    </div>
  );
};
interface IOUTree {
  isNewPortal?: boolean;
  hasPermissionScope?: boolean;
  customClass?: string;
}
export const OUTree = ({
  isNewPortal,
  hasPermissionScope,
  customClass
}: IOUTree) => {
  const [open, setOpen] = useState<boolean>(false);
  const ouDataStatus = useAppSelector(selectOUStatus);
  const ouData = useAppSelector(selectOU);
  const preSelectedNode = useAppSelector(getPreSelectedNode);
  const [selectedNode, setSelectedNode] = useState<TreeNodeData>({
    label: 'OU/Account',
    rootNode: true
  });
  useEffect(() => {
    if (preSelectedNode) {
      setSelectedNode(preSelectedNode);
    }
  }, [preSelectedNode]);
  const trigger = useRef<WhisperInstance>(null);
  const renderSupport = useCallback(
    ({ onClose, left, top, className }: any, ref: any) => {
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <div
            style={{
              minWidth: '600px',
              padding: '20px',
              maxHeight: 'calc(100vh - 200px)',
              overflow: 'auto'
            }}
            className={customClass}
          >
            <OUTreeContent
              setOpen={() => onClose()}
              selectedNode={selectedNode}
              isNewPortal={isNewPortal}
              hasPermissionScope={hasPermissionScope}
            ></OUTreeContent>
          </div>
        </Popover>
      );
    },
    [ouData, selectedNode]
  );

  return (
    <>
      {ouDataStatus === 'succeeded' && ouData?.length ? (
        <>
          <Whisper
            placement="bottomEnd"
            trigger="click"
            ref={trigger}
            speaker={renderSupport}
          >
            <Button appearance="link">
              <Stack alignItems="center" spacing={6}>
                {getNodeIcon(selectedNode)}
                <span color="inherit">{`${
                  selectedNode?.data?.account_email || selectedNode?.label
                }`}</span>
              </Stack>
              <ArrowDownIcon />
            </Button>
          </Whisper>
        </>
      ) : (
        <></>
      )}
    </>
  );
};
